import React, { useMemo } from "react";
import { FolderOutlined } from "@ant-design/icons";
import { BaseSelect } from "components/AntDesign/Select/BaseSelect";

import type { ProjectNode } from "../model/types/ProjectTree";

interface ProjectOption {
  value: number;
  label: string;
}

interface ProjectSelectorProps {
  projects: ProjectNode[];
  value: { id: number; label: string } | null;

  disabled?: boolean;
  loadingMore?: boolean;

  hasNextPage?: boolean;
  loadNextPage?: () => void;
  onSearch?: (value: string) => void;

  onChange: (project: { id: number; label: string } | null) => void;
}

export const ProjectSelector: React.FC<ProjectSelectorProps> = ({
  projects,
  value,
  disabled = false,
  loadingMore = false,
  hasNextPage = false,
  loadNextPage,
  onSearch,
  onChange,
}) => {
  const options = useMemo<ProjectOption[]>(
    () =>
      projects.map((project) => ({
        value: project.id,
        label: project.label,
      })),
    [projects],
  );

  const optionsById = useMemo(
    () => new Map(options.map((option) => [option.value, option.label])),
    [options],
  );

  return (
    <BaseSelect<number>
      value={value?.id}
      options={options}
      filterOption={false}
      onSearch={onSearch}
      prefix={<FolderOutlined />}
      float={{ label: "Search project by name" }}
      disabled={disabled}
      onClear={() => {
        onSearch?.("");
        onChange(null);
      }}
      onChange={(id) => {
        if (!id) {
          onChange(null);
          return;
        }

        onChange({
          id,
          label: optionsById.get(id) ?? value?.label ?? "",
        });
      }}
      hasNextPage={hasNextPage}
      loadNextPage={loadNextPage}
      loadingMore={loadingMore}
    />
  );
};
